/**
 * Mock Unkey service for local development and testing
 * This provides API key functionality without requiring Unkey credentials
 */

// In-memory storage for mock keys
let mockKeys = new Map();

// Initialize with some sample data for testing
const initializeSampleData = () => {
  const sampleKeys = [
    {
      id: 'key_sample_1',
      key: 'arms_sample_key_1_xxxxx',
      keyId: 'key_sample_1',
      apiId: 'mock_api',
      ownerId: 'user_2abc123def456',
      createdAt: new Date(Date.now() - 86400000).toISOString(), // 1 day ago
      meta: {
        userId: 'user_2abc123def456',
        userName: 'Sample User',
        userEmail: 'sample@example.com',
        createdAt: new Date(Date.now() - 86400000).toISOString(),
        mock: true,
        name: 'Sample API Key 1'
      },
      remaining: 950,
      total: 1000
    },
    {
      id: 'key_sample_2',
      key: 'arms_sample_key_2_xxxxx',
      keyId: 'key_sample_2',
      apiId: 'mock_api',
      ownerId: 'user_2abc123def456',
      createdAt: new Date(Date.now() - 172800000).toISOString(), // 2 days ago
      meta: {
        userId: 'user_2abc123def456',
        userName: 'Sample User',
        userEmail: 'sample@example.com',
        createdAt: new Date(Date.now() - 172800000).toISOString(),
        mock: true,
        name: 'Sample API Key 2'
      },
      remaining: 750,
      total: 1000
    }
  ];
  
  sampleKeys.forEach(key => {
    mockKeys.set(key.keyId, key);
  });
  
  console.log('Initialized sample API keys for testing');
};

// Initialize sample data
initializeSampleData();

/**
 * Generate a mock API key
 * @param {string} userId - The user ID from Clerk
 * @param {string} userName - The user's name
 * @param {string} userEmail - The user's email
 * @returns {Promise<Object>} - The generated API key details
 */
export const generateMockApiKey = async (userId, userName, userEmail) => {
  try {
    // Generate a random key with arms_ prefix
    const keyId = `key_${Math.random().toString(36).substr(2, 9)}`;
    const key = `arms_${Math.random().toString(36).substr(2, 16)}`;
    
    const keyData = {
      id: keyId,
      key: key,
      keyId: keyId,
      apiId: 'mock_api',
      ownerId: userId,
      createdAt: new Date().toISOString(),
      meta: {
        userId,
        userName,
        userEmail,
        createdAt: new Date().toISOString(),
        mock: true
      },
      remaining: null,
      total: 0
    };
    
    // Store the key
    mockKeys.set(keyId, keyData);
    
    console.log('Generated mock API key:', keyData);
    
    return {
      key: keyData.key,
      keyId: keyData.keyId,
      meta: keyData.meta,
    };
  } catch (error) {
    console.error('Error generating mock API key:', error);
    throw new Error('Failed to generate API key');
  }
};

/**
 * List all mock API keys for a user
 * @param {string} userId - The user ID from Clerk
 * @returns {Promise<Array>} - Array of API keys
 */
export const listMockApiKeys = async (userId) => {
  try {
    // For demo purposes, return sample keys for any user
    // In production, this should filter by userId
    const userKeys = Array.from(mockKeys.values())
      .map(key => ({
        id: key.id,
        key: key.key,
        keyId: key.keyId,
        createdAt: key.createdAt,
        meta: key.meta,
        remaining: key.remaining,
        total: key.total
      }));
    
    console.log('Listed mock API keys:', userKeys);
    return userKeys;
  } catch (error) {
    console.error('Error listing mock API keys:', error);
    throw new Error('Failed to list API keys');
  }
};

/**
 * Revoke a mock API key
 * @param {string} keyId - The key ID to revoke
 * @returns {Promise<boolean>} - Success status
 */
export const revokeMockApiKey = async (keyId) => {
  try {
    const deleted = mockKeys.delete(keyId);
    console.log('Revoked mock API key:', keyId, deleted);
    return deleted;
  } catch (error) {
    console.error('Error revoking mock API key:', error);
    throw new Error('Failed to revoke API key');
  }
};

/**
 * Get mock API key usage statistics
 * @param {string} keyId - The key ID to get stats for
 * @returns {Promise<Object>} - Usage statistics
 */
export const getMockApiKeyUsage = async (keyId) => {
  try {
    const key = mockKeys.get(keyId);
    if (!key) {
      throw new Error('Key not found');
    }
    
    return {
      keyId: key.keyId,
      remaining: key.remaining,
      total: key.total,
      lastUsed: key.createdAt,
      meta: key.meta,
    };
  } catch (error) {
    console.error('Error getting mock API key usage:', error);
    throw new Error('Failed to get API key usage');
  }
};

/**
 * Verify a mock API key
 * @param {string} key - The API key to verify
 * @returns {Promise<Object>} - Verification result
 */
export const verifyMockApiKey = async (key) => {
  try {
    const keyData = Array.from(mockKeys.values())
      .find(k => k.key === key);
    
    if (keyData) {
      return {
        valid: true,
        ownerId: keyData.ownerId,
        meta: keyData.meta,
      };
    } else {
      return {
        valid: false,
        error: 'Invalid key'
      };
    }
  } catch (error) {
    console.error('Error verifying mock API key:', error);
    throw new Error('Failed to verify API key');
  }
}; 